import Button from "@/components/bricks/button/Button";
import Modal, { ModalContext, ModalTrigger } from "../../../../../Modal/Modal";
import EditIcon from "../../../../../icons/EditIcon";
import { Field, Formik } from "formik";
import useCountries from "../../../../../../hooks/useCountries";
import { useContext, useEffect, useRef, useState } from "react";
import {
  beliggenhedsadresseToLine,
  adressevaelgerToAdresseDTO,
} from "../../../../../../util/adresse";
import { Adresse, FirmaDto, Maybe } from "../../../../../../interfaces/schema";
import { StickyFooter } from "../../../../../stickyFooter/modalStickyFooter";
import LabelledFieldWithValidation from "../../../../../formcontrols/fields/LabelledFieldWithValidation";
import SuperDropdown from "../../../../../dropdown/SuperDropdown";
import CloseIcon from "../../../../../icons/CloseIcon";
import DefaultDropdownItem from "../../../../../dropdown/DefaultDropdownItem";
import IsRequired from "../../../../../formcontrols/common/IsRequired";
import ValidationErrorMessage from "../../../../../formcontrols/ValidationErrorMessage";
import AutoCompleteAddress from "../../../../../autocomplete/AutoCompleteAddress";
import { get } from "../../../../../../api/util";
import { containsOnlyWhitespace } from "../../../../../../util/validation";

interface IFirmaEditBaseProps {
  currentBeliggenhedsAdresse: Maybe<Adresse> | undefined;
  setCurrentBeliggenhedsAdresse: (adresse: Adresse) => void;
  kommuneNr?: number;
  retskredsNr?: number;
  setKommuneNr: (kommuneNr: number | undefined) => void;
  setRetskredsNr: (retskredsNr: number | undefined) => void;
}
interface IFirmaEditModalProps extends IFirmaEditBaseProps {
  firma: FirmaDto;
  adressIsEditable: boolean;
}
interface IFirmaEditContentProps extends IFirmaEditBaseProps {
  setModalOpen: (open: boolean) => void;
}
type AdresseModalFormik = {
  darAdresseId: string | undefined;
  adresselinie1: string;
  adresselinie2?: string | undefined;
  postnummer: string;
  bynavn: string;
  adresselinie4?: string | undefined;
  landekode: string;
  coNavn?: string | undefined;
  kommuneNr?: number;
  retskredsNr?: number;
};

function ModalContent(props: IFirmaEditContentProps) {
  const {
    currentBeliggenhedsAdresse,
    setCurrentBeliggenhedsAdresse,
    setModalOpen,
    kommuneNr,
    retskredsNr,
    // setKommuneNr,
    // setRetskredsNr,
  } = props;

  const { countries, getCountryByCountryCode } = useCountries();
  const [shouldCaptureFocusOnChange, setCaptureFocusOnChange] = useState(false);
  const [countryShouldCaptureFocusOnChange, setCountryCaptureFocusOnChange] =
    useState(false);
  return (
    <Modal
      title={
        <>
          <EditIcon /> Redigér firmaadresse
        </>
      }
      description=""
    >
      <div className="c-modal-container">
        <Formik<AdresseModalFormik>
          initialValues={{
            darAdresseId: currentBeliggenhedsAdresse?.darAdresseId ?? undefined,
            adresselinie1: currentBeliggenhedsAdresse?.adresselinie1 ?? "",
            adresselinie2:
              currentBeliggenhedsAdresse?.adresselinie2 ?? undefined,
            postnummer: currentBeliggenhedsAdresse?.postnummer ?? "",
            bynavn: currentBeliggenhedsAdresse?.bynavn ?? "",
            adresselinie4:
              currentBeliggenhedsAdresse?.adresselinie4 ?? undefined,
            landekode: currentBeliggenhedsAdresse?.landekode ?? "",
            kommuneNr,
            retskredsNr,
            coNavn: currentBeliggenhedsAdresse?.coNavn ?? undefined,
          }}
          validate={(values: AdresseModalFormik) => {
            const errors: DeepPartial<AdresseModalFormik> = {};
            if (!values.adresselinie1) {
              errors.adresselinie1 = "Adresselinje 1 skal udfyldes";
            }
            if (
              !values.postnummer ||
              containsOnlyWhitespace(values.postnummer)
            ) {
              errors.postnummer = "Postnummer skal udfyldes";
            }
            if (!values.bynavn || containsOnlyWhitespace(values.bynavn)) {
              errors.bynavn = "By skal udfyldes";
            }
            if (!values.landekode) {
              errors.landekode = "Landekode skal udfyldes";
            } else if (!getCountryByCountryCode(values.landekode)) {
              errors.landekode = "Ugyldig landekode";
            }
            if (values?.landekode === "DK" && !values.darAdresseId) {
              errors.darAdresseId = "DAR-adresse kræves for danske adresser";
            }
            return errors;
          }}
          onSubmit={(values) => {
            setCurrentBeliggenhedsAdresse({
              adresselinie1: values.adresselinie1,
              adresselinie2: values.adresselinie2,
              adresselinie4: values.adresselinie4,
              bynavn: values.bynavn,
              coNavn: values.coNavn,
              darAdresseId: values.darAdresseId,
              landekode: values.landekode,
              postnummer: values.postnummer,
              toLines: values.adresselinie2
                ? [values.adresselinie1, values.adresselinie2]
                : [values.adresselinie1],
            });
            setModalOpen(false);
          }}
        >
          {(formik) => {
            const { values } = formik;
            const clearBeliggenhedsadresse = (inkluderLand: boolean) => {
              setCaptureFocusOnChange(true);
              formik.setValues(
                {
                  ...formik.values,
                  darAdresseId: undefined,
                  adresselinie1: "",
                  adresselinie2: undefined,
                  postnummer: "",
                  bynavn: "",
                  adresselinie4: undefined,
                  coNavn: undefined,
                  ...(inkluderLand ? { landekode: "" } : {}),
                },
                true,
              );
            };
            return (
              <>
                <div className="c-item-list c-modal-container-inner">
                  <div className="flex-100">
                    <Field name={"landekode"}>
                      {({ form, meta }) => {
                        return (
                          <>
                            <label htmlFor={"landekode"} className="form-label">
                              Land <IsRequired />
                            </label>
                            {values.landekode ? (
                              <CurrentCountryButton
                                onClick={() => {
                                  setCountryCaptureFocusOnChange(true);
                                  if (values.landekode === "DK") {
                                    clearBeliggenhedsadresse(true);
                                  } else form.setFieldValue("landekode", "");
                                }}
                                text={
                                  getCountryByCountryCode(values.landekode)
                                    ?.Navn ?? ""
                                }
                                focusOnMount={countryShouldCaptureFocusOnChange}
                              />
                            ) : (
                              <SuperDropdown
                                classNameContainer="w-100"
                                items={countries}
                                itemsIdKey="Id"
                                itemToString={(item) => item?.Navn ?? ""}
                                itemToElement={(item) => (
                                  <DefaultDropdownItem label={item.Navn} />
                                )}
                                filterKeys={["Navn", "Landekode"]}
                                placeholder="Vælg land ..."
                                onSelectedItemEvent={(item) => {
                                  if (item.selectedItem) {
                                    setCountryCaptureFocusOnChange(true);
                                    form.setFieldTouched("landekode", true);
                                    form.setFieldValue(
                                      "landekode",
                                      item.selectedItem?.Landekode,
                                    );
                                  }
                                }}
                                focusInputOnMount={
                                  countryShouldCaptureFocusOnChange
                                }
                              />
                            )}
                            <ValidationErrorMessage
                              touched={meta.touched}
                              errorMessage={meta.error}
                            />
                          </>
                        );
                      }}
                    </Field>
                  </div>

                  {formik.values.landekode === "DK" ? (
                    <div className="flex-100">
                      {/* <LabelledFieldWithValidation
                        id="darAdresseId"
                        label="DAR-adresseid"
                        name="darAdresseId"
                        // isRequired={true}
                      /> */}
                      <Field name="darAdresseId">
                        {({ form, meta }) => {
                          return (
                            <>
                              <label
                                htmlFor={"darAdresseId"}
                                className="form-label"
                              >
                                Adresse <IsRequired />
                              </label>
                              {formik.values.darAdresseId ? (
                                <CurrentDarAdresseButton
                                  onClick={() =>
                                    clearBeliggenhedsadresse(false)
                                  }
                                  adresseText={beliggenhedsadresseToLine({
                                    ...form.values,
                                  })}
                                  focusOnMount={shouldCaptureFocusOnChange}
                                />
                              ) : (
                                <AutoCompleteAddress
                                  onBlur={() => {
                                    form.setFieldTouched("darAdresseId");
                                  }}
                                  selectedItem={undefined}
                                  classNameContainer={`w-100${
                                    meta.touched && meta.error
                                      ? " is-invalid"
                                      : ""
                                  }`}
                                  onSelectedItemChange={async (adresse) => {
                                    setCaptureFocusOnChange(true);
                                    const dto =
                                      adressevaelgerToAdresseDTO(adresse);
                                    const {
                                      adresselinie1,
                                      bynavn,
                                      postnummer,
                                      darAdresseId,
                                    } = dto;
                                    const { kommuneNr, retskredsNr } =
                                      await get<{
                                        kommuneNr: number;
                                        retskredsKode: string;
                                        retskredsNr: number;
                                      }>({
                                        query: `grunddata/admin?darId=${darAdresseId}`,
                                      });
                                    form.setValues(
                                      {
                                        ...form.values,
                                        darAdresseId,
                                        adresselinie1,
                                        bynavn,
                                        postnummer,
                                        retskredsNr,
                                        kommuneNr,
                                      },
                                      true,
                                    );
                                  }}
                                  focusOnMount={shouldCaptureFocusOnChange}
                                />
                              )}

                              <ValidationErrorMessage
                                touched={meta.touched}
                                errorMessage={meta.error}
                              />
                            </>
                          );
                        }}
                      </Field>
                    </div>
                  ) : (
                    <>
                      <div className="flex-100">
                        <LabelledFieldWithValidation
                          id="adresselinie1"
                          label="Adresselinje 1"
                          name="adresselinie1"
                          isRequired={true}
                        />
                      </div>
                      <div className="flex-100">
                        <LabelledFieldWithValidation
                          id="adresselinie2"
                          label="Adresselinje 2"
                          name="adresselinie2"
                        />
                      </div>
                      <div className="flex-100">
                        <LabelledFieldWithValidation
                          id="postnummer"
                          label="Postnummer"
                          name="postnummer"
                          isRequired={true}
                        />
                      </div>
                      <div className="flex-100">
                        <LabelledFieldWithValidation
                          id="bynavn"
                          label="By"
                          name="bynavn"
                          isRequired={true}
                        />
                      </div>
                      <div className="flex-100">
                        <LabelledFieldWithValidation
                          id="adresselinie4"
                          label="Adresselinje 4"
                          name="adresselinie4"
                        />
                      </div>

                      <div className="flex-100">
                        <LabelledFieldWithValidation
                          id="coNavn"
                          label="C/O"
                          name="coNavn"
                        />
                      </div>
                    </>
                  )}
                </div>
                <StickyFooter>
                  <Button
                    type="button"
                    color="secondary"
                    onClick={() => {
                      setModalOpen(false);
                      formik.resetForm();
                    }}
                  >
                    Annuller
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    onClick={() => {
                      formik.handleSubmit();
                    }}
                    disabled={formik.isSubmitting}
                  >
                    Gem
                  </Button>
                </StickyFooter>
              </>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
}

const FirmaEditAddressModal = ({
  adressIsEditable,
  firma,
  currentBeliggenhedsAdresse,
  ...rest
}: IFirmaEditModalProps) => {
  const { setModalOpen, modalOpen } = useContext(ModalContext);

  return (
    <>
      {adressIsEditable && !firma.cvrNr && (
        <div className="flex-100">
          <h5 className="form-label">Adresse</h5>
          <ModalTrigger asChild>
            <Button
              type="button"
              className="btn--action w-100 fw-medium text-dark py-2 justify-content-between"
              color="transparent"
              onClick={() => setModalOpen(true)}
            >
              <span>
                {beliggenhedsadresseToLine(currentBeliggenhedsAdresse)}
              </span>
              <span className="text-primary">
                <EditIcon />
              </span>
            </Button>
          </ModalTrigger>

          <ModalContent
            key={modalOpen.toString()}
            currentBeliggenhedsAdresse={currentBeliggenhedsAdresse}
            setModalOpen={setModalOpen}
            {...rest}
          />
        </div>
      )}
    </>
  );
};

export default FirmaEditAddressModal;

function CurrentDarAdresseButton(props: {
  onClick: () => void;
  adresseText: string;
  focusOnMount: boolean;
}) {
  const { onClick, adresseText, focusOnMount } = props;
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusOnMount) {
      ref.current?.focus();
    }
  }, [focusOnMount]);
  return (
    <button
      ref={ref}
      type="button"
      className="form-control d-flex align-items-center justify-content-between"
      onClick={onClick}
    >
      <span>{adresseText}</span>
      <CloseIcon />
    </button>
  );
}

function CurrentCountryButton(props: {
  onClick: () => void;
  focusOnMount: boolean;
  text: string;
}) {
  const { focusOnMount, onClick, text } = props;
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusOnMount) {
      ref.current?.focus();
    }
  }, [focusOnMount]);
  return (
    <button
      ref={ref}
      type="button"
      className="form-control d-flex align-items-center justify-content-between"
      onClick={onClick}
    >
      <span>{text}</span>
      <CloseIcon />
    </button>
  );
}
